import React from 'react';
import { Controller } from 'react-hook-form';
import { Select, Space, Tooltip } from 'antd';
import { InfoCircleFilled } from '@ant-design/icons';
import clsx from 'clsx';
import { GenericBadge } from '../../../pages/dataInputs/tiers/components';

const { Option } = Select;

const selectStyles = `
  .ant-select-selector {
    height: 38px !important;
    min-height: 38px !important;
    display: flex !important;
    align-items: center !important;
  }

  .ant-select-selection-item {
    line-height: 46px !important;
    display: flex !important;
    align-items: center !important;
  }
`;

interface TierOption {
  value: string;
  label: string;
  color: string;
}

interface TierSelectFieldProps {
  name: string;
  label?: string;
  control: any;
  rules?: any;
  defaultValue?: string;
  options?: TierOption[];
  disabled?: boolean;
  spaceBetween?: boolean;
  tooltip?: string;
  errors?: any;
  className?: string;
}

const TierSelectField: React.FC<TierSelectFieldProps> = ({
  name,
  label,
  control,
  rules,
  defaultValue = '',
  options = [],
  disabled = false,
  spaceBetween = false,
  tooltip,
  errors,
  className,
}) => {
  const renderErrorMessage = () => {
    const error = errors?.[name];
    if (!error) return null;

    switch (error.type) {
      case 'required':
        return error.message || 'This field is required';
      case 'pattern':
        return error.message || 'Invalid pattern';
      case 'minLength':
        return error.message || 'Value is too short';
      default:
        return 'Invalid value';
    }
  };

  return (
    <Space
      direction="vertical"
      size={4}
      className={clsx('relative w-full my-2.5 font-montserrat', className)}
    >
      <style>{selectStyles}</style>
      <div
        className={clsx('w-full flex items-center gap-4', {
          'justify-between': spaceBetween,
        })}
      >
        <div className="flex items-center gap-2">
          <label
            htmlFor={name}
            className="font-semibold text-primary-100 text-[14px]"
          >
            {label}
          </label>
          {tooltip && (
            <Tooltip title={tooltip}>
              <InfoCircleFilled className="text-[#293241]" />
            </Tooltip>
          )}
        </div>
        <div
          className={clsx('relative flex', {
            'flex-1': !spaceBetween,
          })}
        >
          <Controller
            control={control}
            name={name}
            rules={rules}
            defaultValue={defaultValue}
            render={({ field }) => (
              <Select
                {...field}
                id={name}
                disabled={disabled}
                status={errors?.[name] ? 'error' : ''}
                className={clsx('min-w-[100px]', {
                  'border-danger': errors?.[name],
                })}
                value={field.value || undefined}
                onChange={(value) => field.onChange(value)}
                placeholder="Select a tier"
                showSearch={false}
                allowClear
              >
                {options && options.length > 0 ? (
                  options.map((option: TierOption, index: number) => (
                    <Option
                      key={index}
                      value={option.value}
                      label={option.label}
                    >
                      <div className="flex items-center gap-2">
                                              {GenericBadge ? (
                        <GenericBadge
                          name={option.label}
                          color={option.color}
                          style={{ margin: 0 }}
                        />
                      ) : (
                        <span style={{ color: option.color, border: `1px solid ${option.color}`, padding: '2px 6px', borderRadius: '4px' }}>
                          {option.label}
                        </span>
                      )}
                      </div>
                    </Option>
                  ))
                ) : (
                  <Option value="" disabled>
                    No options available
                  </Option>
                )}
              </Select>
            )}
          />
        </div>
      </div>
      {errors?.[name] && (
        <span className="flex items-center justify-end text-danger text-sm">
          {renderErrorMessage()}
        </span>
      )}
    </Space>
  );
};

export default TierSelectField;